import { useCallback, useMemo } from "react";
import { Checkbox } from "semantic-ui-react";

import { queryClient } from "App";
import CampaignService from "services/Campaign";

/**
 * @param {Object} params
 * @param {string} params.queryKey Single cache key segment, not an array. It is
 *   wrapped before invalidation, so passing an array would nest it and match
 *   nothing.
 */
export function useModalContactTable({
  entityId,
  campaignId,
  queryKey,
  onInclude,
  onSuccess,
  includeAll = false,
  contactIds = [],
}) {
  const invalidateContactsQuery = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: [queryKey] });
  }, [queryKey]);

  const handleInclude = useCallback(
    async (contactId, checked) => {
      if (typeof onInclude === "function") {
        onInclude(contactId, checked);
        return;
      }

      await CampaignService[checked ? "addContact" : "removeContact"](
        campaignId,
        entityId,
        contactId
      );
      await invalidateContactsQuery();
      onSuccess?.();
    },
    [campaignId, entityId, invalidateContactsQuery, onInclude, onSuccess]
  );

  const columns = useMemo(() => {
    return [
      {
        header: "Name",
        id: "Name",
        accessorKey: "full_name",
      },
      {
        header: "Email",
        id: "Email",
        accessorKey: "email",
      },
      {
        header: "Include in campaign",
        id: "Include in campaign",
        accessorKey: "campaign_targets",
        enableSorting: false,
        cell: ({ row }) => (
          <Checkbox
            toggle
            name={`${row.original.id}in_campaign_${campaignId}`}
            checked={
              (includeAll && !contactIds.includes(-row.original?.id)) ||
              (contactIds || []).includes(row.original?.id) ||
              row.original?.campaign_targets?.some(
                ct => ct.campaign_id == campaignId
              )
            }
            onClick={(_, { checked }) =>
              handleInclude(row.original?.id, checked)
            }
          />
        ),
      },
    ];
  }, [campaignId, contactIds, includeAll, handleInclude]);

  return {
    columns,
  };
}
